import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { getCampaigns, getRev, getMap, getFactions, getMembers, joinCampaign, getArmies, setOrder, planPath, advanceDay, patchArmy, patchFaction, getRecruitments, seedTreasury, recruit, raidArmy, standDown, provisionArmy, getSupplyBases, getCaravans, getCampFollowers, resupplyBase, dispatchCaravan, previewDispatch, patchSupplyBase, getTroopTypes, setRoster, getProjection, setScouting, setFormation, revealArmy, setDetachment, disperseFollowers, getAccessCodes, setAccessCodes as apiSetAccessCodes, getCommanders, createCommander, patchCommander, deleteCommander, seedCommanders,
  getStrongholds, getSieges, seedStrongholds, patchStronghold, setSpecialists as apiSetSpecialists,
  setSiegeAllocation, besiege, liftSiege, assaultSiege, sallySiege, setSiegeTerms, resolveSiege,
  rollSurrenderCheck } from '../api'
import { getStoredUser, ensureUser } from '../identity'
import CampaignMap from '../components/CampaignMap'
import ArmyPanel from '../components/ArmyPanel'
import RecruitModal from '../components/RecruitModal'
import CampaignSettingsModal from '../components/CampaignSettingsModal'
import LedgerView from '../components/LedgerView'
import CampaignPicker from './CampaignPicker'
import { C, muteColor } from '../theme'

export default function Campaign() {
  const { campaignId: campaignIdParam } = useParams()
  const campaignId = Number(campaignIdParam) // the URL is the source of truth for which campaign
  const [map, setMap] = useState(null)
  const [error, setError] = useState(null)
  const [campaigns, setCampaigns] = useState(null) // null until the list loads
  const [factions, setFactions] = useState([])
  const [members, setMembers] = useState([])
  const [armies, setArmies] = useState([])
  const [recruitments, setRecruitments] = useState([])
  const [supplyBases, setSupplyBases] = useState([])
  const [caravans, setCaravans] = useState([])
  const [campFollowers, setCampFollowers] = useState([])
  const [commandersData, setCommandersData] = useState(null) // { commanders, substitutesByFaction, pointBuy }
  const [strongholds, setStrongholds] = useState([])  // the 33 forts/cities seeded from the map
  const [sieges, setSieges] = useState([])            // live + finished sieges, decorated server-side
  const [user, setUser] = useState(getStoredUser())
  const [troopTypes, setTroopTypes] = useState([])
  const [day, setDay] = useState(1)

  // interaction state machine
  const [mode, setMode] = useState('idle') // idle | placing | pathing
  const [selectedArmyId, setSelectedArmyId] = useState(null)
  const [plannedPath, setPlannedPath] = useState([])
  const [plannedCost, setPlannedCost] = useState(0) // movement cost of the planned route
  const [preview, setPreview] = useState(null)
  const [scoutEvents, setScoutEvents] = useState(null) // scouting detections/contacts from the last preview
  const [scoutAudit, setScoutAudit] = useState(null) // per-army scouting reasoning (dice log) from the last preview
  const [showLedger, setShowLedger] = useState(false)
  const [showRecruit, setShowRecruit] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [accessCodes, setAccessCodes] = useState(null) // { dmCode, factions:[{id,name,color,code}] } — DM only, loaded when Settings opens
  const [projected, setProjected] = useState(null) // { factionId: tomorrowsTreasury } from a dry-run
  const [notice, setNotice] = useState(null) // { kind:'error'|'info', msg } — quiet, non-blocking

  // Quiet, non-blocking replacement for alert(): a transient toast that auto-dismisses.
  const notify = useCallback((kind, msg) => {
    setNotice({ kind, msg, at: Date.now() })
  }, [])
  useEffect(() => {
    if (!notice) return
    const t = setTimeout(() => setNotice(n => (n && n.at === notice.at ? null : n)), notice.kind === 'error' ? 5000 : 3000)
    return () => clearTimeout(t)
  }, [notice])

  // Fog of war: pass the viewer's uuid so the server projects reads to what this faction may see.
  const viewerUuid = user && user.uuid
  const loadCampaign = useCallback((id) => {
    setSelectedArmyId(null); setMode('idle'); setPlannedPath([]); setPlannedCost(0); setPreview(null); setProjected(null)
    return Promise.all([getMap(id), getFactions(id), getMembers(id), getArmies(id, viewerUuid), getRecruitments(id, viewerUuid), getSupplyBases(id, viewerUuid), getCaravans(id, viewerUuid), getCampFollowers(id, viewerUuid), getCommanders(id, viewerUuid)])
      .then(([m, f, mem, ar, rec, sb, cv, cf, cm]) => {
        setMap(m); setFactions(f); setMembers(mem); setArmies(ar); setRecruitments(rec); setSupplyBases(sb); setCaravans(cv); setCampFollowers(cf); setCommandersData(cm); setDay(m.campaign.current_day)
      })
      .catch(e => setError(e.message))
  }, [viewerUuid])

  // Load the campaign list once (for validating the URL id and the picker fallback).
  useEffect(() => {
    let cancelled = false
    getCampaigns()
      .then(list => { if (!cancelled) setCampaigns(list || []) })
      .catch(e => { if (!cancelled) setError(e.message) })
    return () => { cancelled = true }
  }, [])

  // Load (or switch to) the campaign named in the URL, once we know which ones exist.
  const campaignExists = campaigns != null && campaigns.some(c => c.id === campaignId)
  useEffect(() => {
    if (campaignExists) { setMap(null); loadCampaign(campaignId) }
  }, [campaignExists, campaignId, loadCampaign])

  useEffect(() => { if (getStoredUser()) ensureUser().then(setUser).catch(() => {}) }, [])
  useEffect(() => { getTroopTypes().then(setTroopTypes).catch(() => {}) }, [])

  // Live "tomorrow" projection: whenever the Ledger is open, re-run the dry-run after any state
  // change (day/armies/factions), debounced so a burst of edits collapses into one call.
  useEffect(() => {
    if (!showLedger || campaignId == null) return
    let cancelled = false
    const t = setTimeout(() => {
      getProjection(campaignId)
        .then(d => { if (!cancelled && d && d.projectedFactions) setProjected(Object.fromEntries(d.projectedFactions.map(f => [f.id, f.treasury]))) })
        .catch(() => {})
    }, 300)
    return () => { cancelled = true; clearTimeout(t) }
  }, [showLedger, campaignId, day, armies, factions])

  // Live sync. This client re-fetches only on its OWN actions, so anything another actor changes —
  // an ally recruiting a new army, or the DM advancing the day and moving armies — would otherwise
  // never appear here. Every 3s we hit the tiny `/rev` change-token; only when the revision moved do
  // we run the heavier per-viewer refetch (fog stays correct — each fetch carries this viewer's
  // uuid), and even then we apply a result only if it truly changed (no needless map repaint / no
  // clobbering an in-progress edit). Idle cost is one small request per pulse; a real change
  // surfaces in ≤ ~3s. Paused while the tab is hidden.
  const lastRevRef = useRef(null)
  useEffect(() => {
    if (campaignId == null || !campaignExists) return
    let stop = false
    lastRevRef.current = null // force one sync on (re)mount / campaign switch
    const applyIfChanged = (setter) => (next) => setter(prev => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next))
    const fullRefresh = async () => {
      const [ar, f, mem, rec, sb, cv, cf, cm, sh, sg] = await Promise.all([
        getArmies(campaignId, viewerUuid), getFactions(campaignId), getMembers(campaignId), getRecruitments(campaignId, viewerUuid),
        getSupplyBases(campaignId, viewerUuid), getCaravans(campaignId, viewerUuid), getCampFollowers(campaignId, viewerUuid),
        getCommanders(campaignId, viewerUuid), getStrongholds(campaignId, viewerUuid), getSieges(campaignId, viewerUuid),
      ])
      if (stop) return
      applyIfChanged(setArmies)(ar); applyIfChanged(setFactions)(f); applyIfChanged(setMembers)(mem); applyIfChanged(setRecruitments)(rec)
      applyIfChanged(setSupplyBases)(sb); applyIfChanged(setCaravans)(cv); applyIfChanged(setCampFollowers)(cf)
      applyIfChanged(setCommandersData)(cm)
      applyIfChanged(setStrongholds)(sh); applyIfChanged(setSieges)(sg)
    }
    const tick = async () => {
      if (typeof document !== 'undefined' && document.hidden) return
      try {
        const { rev, day: srvDay } = await getRev(campaignId)
        if (stop) return
        if (srvDay != null) setDay(d => (d === srvDay ? d : srvDay))
        if (rev !== lastRevRef.current) { lastRevRef.current = rev; await fullRefresh() }
      } catch { /* transient network error — retry next pulse */ }
    }
    const iv = setInterval(tick, 3000)
    return () => { stop = true; clearInterval(iv) }
  }, [campaignId, campaignExists, viewerUuid])

  const refreshArmies = useCallback(() => { if (campaignId != null) getArmies(campaignId, viewerUuid).then(setArmies) }, [campaignId, viewerUuid])
  const refreshFactions = useCallback(() => { if (campaignId != null) getFactions(campaignId).then(setFactions) }, [campaignId])
  const refreshRecruitments = useCallback(() => { if (campaignId != null) getRecruitments(campaignId, viewerUuid).then(setRecruitments) }, [campaignId, viewerUuid])
  const refreshLogistics = useCallback(() => {
    if (campaignId == null) return
    getSupplyBases(campaignId, viewerUuid).then(setSupplyBases); getCaravans(campaignId, viewerUuid).then(setCaravans)
    getCampFollowers(campaignId, viewerUuid).then(setCampFollowers)
  }, [campaignId, viewerUuid])

  // ── Phase 5: sieges ───────────────────────────────────────────────────────
  // Every siege mutation refreshes the two listings so the panel redraws from server truth
  // rather than guessing; each returns the raw result so SiegePanel can show what happened.
  const refreshSieges = useCallback(() => {
    if (campaignId == null) return
    getStrongholds(campaignId, viewerUuid).then(setStrongholds).catch(() => {})
    getSieges(campaignId, viewerUuid).then(setSieges).catch(() => {})
  }, [campaignId, viewerUuid])

  const siegeAct = async (fn) => {
    const res = await fn()
    if (res && res.ok === false) setNotice({ kind: 'error', msg: (res.data && res.data.error) || 'that could not be done' })
    refreshSieges(); refreshArmies(); refreshFactions()
    return res
  }
  const onBesiege = (armyId) => siegeAct(() => besiege(armyId, { actingUserUuid: user.uuid }))
  const onLiftSiege = (siegeId) => siegeAct(() => liftSiege(siegeId, { actingUserUuid: user.uuid }))
  const onAssault = (siegeId, body) => siegeAct(() => assaultSiege(siegeId, { actingUserUuid: user.uuid, ...body }))
  const onSally = (siegeId, body) => siegeAct(() => sallySiege(siegeId, { actingUserUuid: user.uuid, ...body }))
  const onSiegeTerms = (siegeId, body) => siegeAct(() => setSiegeTerms(siegeId, { actingUserUuid: user.uuid, ...body }))
  const onResolveSiege = (siegeId, body) => siegeAct(() => resolveSiege(siegeId, { actingUserUuid: user.uuid, ...body }))
  const onSurrenderCheck = (siegeId, body) => siegeAct(() => rollSurrenderCheck(siegeId, { actingUserUuid: user.uuid, ...body }))
  const onSiegeAllocation = (armyId, body) => siegeAct(() => setSiegeAllocation(armyId, { actingUserUuid: user.uuid, ...body }))
  const onSpecialists = (armyId, body) => siegeAct(() => apiSetSpecialists(armyId, { actingUserUuid: user.uuid, ...body }))
  const onPatchStronghold = (id, body) => siegeAct(() => patchStronghold(id, { actingUserUuid: user.uuid, ...body }))
  const onSeedStrongholds = async () => {
    const res = await siegeAct(() => seedStrongholds(campaignId, { actingUserUuid: user.uuid }))
    if (res && res.ok) setNotice({ kind: 'info', msg: `Seeded ${res.data.added} strongholds — ${res.data.cities} cities, ${res.data.fortresses} fortresses.` })
    return res
  }

  const onSetSupply = async (armyId, gp) => {
    const res = await patchArmy(armyId, { actingUserUuid: user.uuid, carried_supply_gp: gp })
    if (res.ok) setArmies(res.data.armies)
  }

  const onProvision = async (armyId, days) => {
    const res = await provisionArmy(armyId, { actingUserUuid: user.uuid, days })
    if (res.ok) { setArmies(res.data.armies); refreshFactions() }
    else notify('error', res.data.error || 'provision failed')
    return res
  }

  const onResupplyBase = async (baseId, gp) => {
    const res = await resupplyBase(baseId, { actingUserUuid: user.uuid, gp })
    if (res.ok) { setSupplyBases(res.data.supplyBases); refreshFactions() }
    else notify('error', res.data.error || 'resupply failed')
  }
  const onDispatchCaravan = async (baseId, targetArmyId, gp) => {
    const res = await dispatchCaravan(baseId, { actingUserUuid: user.uuid, targetArmyId, gp })
    if (res.ok) { setSupplyBases(res.data.supplyBases); setCaravans(res.data.caravans) }
    else notify('error', res.data.error || 'dispatch failed')
  }
  // Read-only: route/ETA/surcharge a dispatch from this base to that army would see.
  const onPreviewDispatch = async (baseId, targetArmyId) => {
    const res = await previewDispatch(baseId, { actingUserUuid: user.uuid, targetArmyId })
    return res.ok ? res.data : null
  }
  const onPatchBase = async (baseId, body) => {
    const res = await patchSupplyBase(baseId, { actingUserUuid: user.uuid, ...body })
    if (res.ok) {
      setSupplyBases(res.data.supplyBases)
      if (res.data.caravans) setCaravans(res.data.caravans)
      refreshFactions()
      const m = res.data.migration
      if (m && m.refundedGp) notify('info', `base closed — ${Math.round(m.refundedGp).toLocaleString()} gp refunded to the treasury`)
      else if (m && m.migratedGp) notify('info', `base closed — ${Math.round(m.migratedGp).toLocaleString()} gp is migrating by caravan`)
    } else notify('error', res.data.error || 'base update failed')
    return res
  }
  // Standing-order settings live on the army row (auto_resupply / resupply_target_days).
  const onSetAutoResupply = async (armyId, fields) => {
    const res = await patchArmy(armyId, { actingUserUuid: user.uuid, ...fields })
    if (res.ok) setArmies(res.data.armies); else notify('error', res.data.error || 'auto-resupply update failed')
  }

  const onSeedTreasury = async (days) => {
    const res = await seedTreasury(campaignId, { actingUserUuid: user.uuid, startingGoldDays: days })
    if (res.ok) setFactions(res.data.factions); else notify('error', res.data.error || 'seed failed')
  }
  const onSetInstantBudget = async (fid, gp) => {
    const res = await patchFaction(campaignId, fid, { actingUserUuid: user.uuid, instant_budget_gp: gp })
    if (res.ok) setFactions(res.data); else notify('error', res.data.error || 'set budget failed')
    return res
  }
  const onSetTreasury = async (fid, gp) => {
    const res = await patchFaction(campaignId, fid, { actingUserUuid: user.uuid, treasury: gp })
    if (res.ok) setFactions(res.data); else notify('error', res.data.error || 'set treasury failed')
    return res
  }

  const onRecruit = async (body) => {
    const res = await recruit(campaignId, { actingUserUuid: user.uuid, ...body })
    if (res.ok) {
      if (res.data.recruitments) setRecruitments(res.data.recruitments)
      if (res.data.armies) setArmies(res.data.armies)
      refreshFactions()
    } else notify('error', res.data.error || 'recruit failed')
    return res
  }

  const myMembership = user ? members.find(m => m.userUuid === user.uuid) : null
  const isDm = myMembership && myMembership.role === 'dm'
  const myFactionId = myMembership && myMembership.factionId
  const canAct = !!(isDm || myFactionId)

  const onIdentify = async (name) => setUser(await ensureUser(name))
  const onJoin = async (body) => {
    if (!user) return { ok: false, status: 401, data: { error: 'enter your name first' } }
    const res = await joinCampaign(campaignId, { actingUserUuid: user.uuid, ...body })
    if (res.ok && res.data.members) setMembers(res.data.members); else getMembers(campaignId).then(setMembers)
    return res
  }
  // Invite links: /campaign?join=<code> prefills the join-code field.
  const pendingJoinCode = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '').get('join') || ''
  // DM: load the plaintext join codes when Settings opens (and refresh if factions' claimed state moves).
  useEffect(() => {
    if (!(showSettings && isDm && user)) { return }
    let cancelled = false
    getAccessCodes(campaignId, user.uuid).then(r => { if (!cancelled && r.ok) setAccessCodes(r.data) }).catch(() => {})
    return () => { cancelled = true }
  }, [showSettings, isDm, campaignId, user, factions])
  const onSetCodes = async (body) => {
    const res = await apiSetAccessCodes(campaignId, { actingUserUuid: user.uuid, ...body })
    if (res.ok) { setAccessCodes(res.data); refreshFactions() }
    return res
  }

  // ── army actions ──
  const onSelectArmy = (id) => { setSelectedArmyId(id); setMode('pathing'); setPlannedPath([]); setPlannedCost(0) }
  const onDeselect = () => { setSelectedArmyId(null); setMode('idle'); setPlannedPath([]); setPlannedCost(0) }
  const onSetRoster = async (armyId, rows, charge = false) => {
    const res = await setRoster(armyId, { actingUserUuid: user.uuid, rows, charge })
    if (res.ok) { setArmies(res.data.armies); refreshFactions() }
    else notify('error', res.data.error || 'save roster failed')
    return res
  }
  const onIssueOrder = async () => {
    const res = await setOrder(selectedArmyId, { actingUserUuid: user.uuid, orderType: 'move', payload: { path: plannedPath } })
    if (res.ok) { setArmies(res.data.armies); setPlannedPath([]); setPlannedCost(0); notify('info', 'Move order issued.') }
    else notify('error', res.data.error || 'order failed')
  }
  const onClearPath = () => { setPlannedPath([]); setPlannedCost(0) }
  const onRaid = async (armyId, stance) => {
    const res = await raidArmy(armyId, { actingUserUuid: user.uuid, stance })
    if (res.ok) {
      setArmies(res.data.armies)
      if (res.data.autoAssignedRaiders > 0) notify('info', `Raid ordered — ${res.data.autoAssignedRaiders} unassigned men committed to the Raiders bucket.`)
    } else notify('error', res.data.error || 'raid failed')
  }
  const onStandDown = async (armyId) => {
    const res = await standDown(armyId, { actingUserUuid: user.uuid })
    if (res.ok) setArmies(res.data.armies); else notify('error', res.data.error || 'stand-down failed')
  }
  const applyDetachmentRes = (res) => {
    if (res.data.armies) setArmies(res.data.armies)
    if (res.data.campFollowers) setCampFollowers(res.data.campFollowers)
  }
  const onSetDetachment = async (armyId, on) => {
    const res = await setDetachment(armyId, { actingUserUuid: user.uuid, on })
    if (res.ok) { applyDetachmentRes(res); notify('info', on ? 'Detachment formed — camp followers left behind.' : 'Camp followers rejoined.') }
    else notify('error', res.data.error || 'detachment change failed')
    return res
  }
  const onDisperseFollowers = async (armyId) => {
    const res = await disperseFollowers(armyId, { actingUserUuid: user.uuid })
    if (res.ok) { applyDetachmentRes(res); notify('info', 'Camp followers dispersed.') }
    else notify('error', res.data.error || 'disperse failed')
    return res
  }
  const onSetScouting = async (armyId, cfg) => {
    const res = await setScouting(armyId, { actingUserUuid: user.uuid, ...cfg })
    if (res.ok) { setArmies(res.data.armies); notify('info', 'Scouting orders set.') }
    else notify('error', res.data.error || 'scouting failed')
    return res
  }
  const onSetFormation = async (armyId, assignments) => {
    const res = await setFormation(armyId, { actingUserUuid: user.uuid, assignments })
    if (res.ok) { setArmies(res.data.armies); notify('info', 'Formation saved.') }
    else notify('error', res.data.error || 'formation save failed')
    return res
  }
  // ── commanders ──
  // Mutation responses carry the fresh roster ({commanders, substitutesByFaction, pointBuy})
  // plus projected armies (commander names / leaderless flags ride on army rows).
  const applyCommanderRes = (res) => {
    if (res.data.commanders) setCommandersData({ commanders: res.data.commanders, substitutesByFaction: res.data.substitutesByFaction, pointBuy: res.data.pointBuy })
    if (res.data.armies) setArmies(res.data.armies)
  }
  const onCreateCommander = async (body) => {
    const res = await createCommander(campaignId, { actingUserUuid: user.uuid, ...body })
    if (res.ok) { applyCommanderRes(res); notify('info', 'Commander created.') }
    else notify('error', res.data.error || 'create commander failed')
    return res.ok
  }
  const onPatchCommander = async (commanderId, body) => {
    const res = await patchCommander(commanderId, { actingUserUuid: user.uuid, ...body })
    if (res.ok) {
      applyCommanderRes(res)
      notify('info', res.data.arriveDay != null ? `Commander riding out — arrives day ${res.data.arriveDay}.` : 'Commander updated.')
    } else notify('error', res.data.error || 'commander update failed')
    return res.ok
  }
  const onDeleteCommander = async (commanderId) => {
    const res = await deleteCommander(commanderId, { actingUserUuid: user.uuid })
    if (res.ok) { applyCommanderRes(res); notify('info', 'Commander deleted.') }
    else notify('error', res.data.error || 'delete failed')
    return res.ok
  }
  const onSeedCommanders = async (perFaction) => {
    const res = await seedCommanders(campaignId, { actingUserUuid: user.uuid, perFaction })
    if (res.ok) { applyCommanderRes(res); notify('info', `Seeded ${res.data.created} commander(s) (target ${res.data.perFaction} per faction).`) }
    else notify('error', res.data.error || 'seed commanders failed')
    return res
  }

  // DM "mark army visible to faction" (and inverse) — from off-band adjudication.
  const onReveal = async (factionId, armyId, on) => {
    const res = await revealArmy(campaignId, { actingUserUuid: user.uuid, factionId, armyId, on })
    if (res.ok) { refreshArmies(); notify('info', on ? 'Revealed to faction.' : 'Hidden from faction.') }
    else notify('error', res.data.error || 'reveal failed')
    return res
  }

  const onTileClick = async (row, col) => {
    if (mode === 'pathing' && selectedArmyId) {
      // Auto-route: a single click on a destination becomes the whole shortest path. Extra
      // clicks add waypoints (route extends from the last tile already planned). The server
      // owns the terrain cost model, so we ask it for the segment rather than re-implementing it.
      const army = armies.find(a => a.id === selectedArmyId)
      if (!army || army.tile_row == null) { notify('error', 'Place this army on the map first.'); return }
      const from = plannedPath.length ? plannedPath[plannedPath.length - 1] : [army.tile_row, army.tile_col]
      if (from[0] === row && from[1] === col) return // clicked the current endpoint — no-op
      const res = await planPath(selectedArmyId, { actingUserUuid: user.uuid, from, row, col })
      if (res.ok && res.data.path && res.data.path.length) {
        setPlannedPath(p => [...p, ...res.data.path])
        setPlannedCost(c => c + (res.data.cost || 0))
      } else {
        notify('error', res.data.error || 'no route to that tile')
      }
      return
    }
    // idle: select an army on this tile
    const hit = armies.find(a => a.tile_row === row && a.tile_col === col)
    if (hit) onSelectArmy(hit.id)
  }

  // ── advance day ──
  const onPreview = async () => {
    const res = await advanceDay(campaignId, { actingUserUuid: user.uuid, commit: false })
    if (res.ok) { setPreview(res.data.log); setScoutEvents(res.data.scoutingEvents || []); setScoutAudit(res.data.scoutingAudit || []) }
    else notify('error', res.data.error || 'preview failed')
  }
  const onCommit = async () => {
    const res = await advanceDay(campaignId, { actingUserUuid: user.uuid, commit: true })
    if (res.ok) { setDay(res.data.day); setPreview(null); setScoutEvents(null); setScoutAudit(null); refreshArmies(); refreshFactions(); refreshRecruitments(); refreshLogistics() }
    else notify('error', res.data.error || 'commit failed')
  }

  // cities the acting faction controls, available as muster points for recruitment
  const musterCities = (factionId) => (map && factionId != null)
    ? map.tiles.filter(t => t[4] && t[3] === factionId).map(t => ({ row: t[0], col: t[1], name: t[7] || null }))
    : []

  if (error) return <div style={{ padding: 24 }}>⚠ {error}</div>
  // Unknown / missing campaign id in the URL → fall back to the picker.
  if (campaigns != null && !campaignExists) return <CampaignPicker notFoundId={campaignIdParam} />
  if (!map) return <div style={{ padding: 24 }}>Loading campaign…</div>

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '9px 18px', borderBottom: `1px solid ${C.ruleStrong}`, background: C.paperRail, boxShadow: `0 1px 0 ${C.rule}` }}>
        <Link to="/" title="Switch campaign"
          style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: 17, letterSpacing: 1, color: C.ink, textDecoration: 'none' }}>TACITVS</Link>
        <strong style={{ fontSize: 16, color: C.inkSoft, fontWeight: 600 }}>{map.campaign.name}</strong>
        <span style={{ padding: '2px 9px', background: C.paperSunken, border: `1px solid ${C.rule}`, borderRadius: 4, color: C.ink }}>Day {day}</span>
        <button onClick={() => setShowLedger(s => !s)}
          style={{ padding: '4px 12px', borderRadius: 4, border: `1px solid ${showLedger ? C.gold : C.rule}`, cursor: 'pointer', fontSize: 13,
            background: showLedger ? C.gold : C.paperRaised, color: showLedger ? C.paperRaised : C.ink, fontFamily: "'Cinzel', serif", letterSpacing: 0.5 }}>
          {showLedger ? '▦ Map' : '▦ Ledger'}
        </button>
        {canAct && (
          <button onClick={() => setShowRecruit(true)}
            style={{ padding: '4px 12px', borderRadius: 4, border: `1px solid ${C.rule}`, cursor: 'pointer', fontSize: 13,
              background: C.paperRaised, color: C.ink, fontFamily: "'Cinzel', serif", letterSpacing: 0.5 }}>
            ⚔ Recruit
          </button>
        )}
        {isDm && (
          <button onClick={() => setShowSettings(true)}
            style={{ padding: '4px 12px', borderRadius: 4, border: `1px solid ${C.rule}`, cursor: 'pointer', fontSize: 13,
              background: C.paperRaised, color: C.ink, fontFamily: "'Cinzel', serif", letterSpacing: 0.5 }}>
            ⚙ Settings
          </button>
        )}
        <Link to="/guide" title="Field Guide / tutorials"
          style={{ padding: '4px 12px', borderRadius: 4, border: `1px solid ${C.rule}`, cursor: 'pointer', fontSize: 13,
            background: C.paperRaised, color: C.ink, fontFamily: "'Cinzel', serif", letterSpacing: 0.5, textDecoration: 'none' }}>
          ❔ Guide
        </Link>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 16 }}>
          {(factions.length ? factions : map.factions).map(f => (
            <span key={f.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: C.ink }}>
              <span style={{ width: 12, height: 12, borderRadius: 2, background: muteColor(f.color), display: 'inline-block', border: `1px solid ${C.shadow}` }} />
              {f.name}
              {f.treasury != null && <span className="tnum" style={{ color: C.inkSoft }}>· {Math.round(f.treasury).toLocaleString()} gp</span>}
            </span>
          ))}
        </div>
      </header>
      {showLedger ? (
        <LedgerView factions={factions} armies={armies} projected={projected}
          onClose={() => setShowLedger(false)} />
      ) : (
      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        <aside style={{ width: 340, flex: '0 0 340px', background: C.paperRail, borderRight: `1px solid ${C.ruleStrong}`, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <ArmyPanel
            isDm={isDm} canAct={canAct} factions={factions} myFactionId={myFactionId}
            armies={armies} selectedArmyId={selectedArmyId} mode={mode} plannedPath={plannedPath} plannedCost={plannedCost} currentDay={day}
            preview={preview}
            supplyBases={supplyBases} caravans={caravans} campFollowers={campFollowers} troopTypes={troopTypes} onSetRoster={onSetRoster}
            onResupplyBase={onResupplyBase} onDispatchCaravan={onDispatchCaravan}
            onPreviewDispatch={onPreviewDispatch} onPatchBase={onPatchBase} onSetAutoResupply={onSetAutoResupply}
            onSelectArmy={onSelectArmy} onDeselect={onDeselect}
            onIssueOrder={onIssueOrder} onClearPath={onClearPath}
            onPreview={onPreview} onCommit={onCommit} onSetSupply={onSetSupply}
            onRecruit={onRecruit} onRaid={onRaid} onStandDown={onStandDown}
            onProvision={onProvision} onSetDetachment={onSetDetachment} onDisperseFollowers={onDisperseFollowers}
            onSetScouting={onSetScouting} onSetFormation={onSetFormation} onReveal={onReveal} scoutEvents={scoutEvents} scoutAudit={scoutAudit}
            commandersData={commandersData} onCreateCommander={onCreateCommander} onPatchCommander={onPatchCommander} onDeleteCommander={onDeleteCommander}
            strongholds={strongholds} sieges={sieges}
            onBesiege={onBesiege} onLiftSiege={onLiftSiege} onAssault={onAssault} onSally={onSally}
            onSiegeTerms={onSiegeTerms} onResolveSiege={onResolveSiege} onSurrenderCheck={onSurrenderCheck}
            onSiegeAllocation={onSiegeAllocation} onSpecialists={onSpecialists}
            onPatchStronghold={onPatchStronghold} onSeedStrongholds={onSeedStrongholds}
            user={user} members={members} myMembership={myMembership} onIdentify={onIdentify} onJoin={onJoin} pendingJoinCode={pendingJoinCode}
          />
        </aside>
        <div style={{ flex: 1, minWidth: 0 }}>
          <CampaignMap map={map} armies={armies} caravans={caravans} campFollowers={campFollowers} supplyBases={supplyBases} strongholds={strongholds} sieges={sieges} selectedArmyId={selectedArmyId} plannedPath={plannedPath} onTileClick={onTileClick} />
        </div>
      </div>
      )}
      {showRecruit && (
        <RecruitModal
          isDm={isDm} factions={factions} myFactionId={myFactionId} armies={armies}
          catalog={troopTypes} musterCities={musterCities} recruitments={recruitments}
          onRecruit={onRecruit} onClose={() => setShowRecruit(false)}
        />
      )}
      {showSettings && isDm && (
        <CampaignSettingsModal
          factions={factions} onSetInstantBudget={onSetInstantBudget}
          onSeedTreasury={onSeedTreasury} onSetTreasury={onSetTreasury} onClose={() => setShowSettings(false)}
          onSeedCommanders={onSeedCommanders}
          accessCodes={accessCodes} onSetCodes={onSetCodes} joinLinkBase={`${window.location.origin}/campaign?join=`}
        />
      )}
      {notice && (
        <div onClick={() => setNotice(null)}
          style={{
            position: 'fixed', bottom: 18, left: '50%', transform: 'translateX(-50%)', zIndex: 50,
            maxWidth: 480, padding: '10px 16px', borderRadius: 6, cursor: 'pointer',
            fontSize: 13.5, color: C.paperRaised, boxShadow: `0 6px 18px ${C.shadow}`,
            background: notice.kind === 'error' ? C.crimson : C.ink,
            border: `1px solid ${notice.kind === 'error' ? '#5e2018' : C.gold}`,
          }}>
          {notice.kind === 'error' ? '⚠ ' : ''}{notice.msg}
        </div>
      )}
    </div>
  )
}
